import { Logger, isBrowser } from '@hot-club-http/client/utils'
import type { QueryClient } from '@tanstack/react-query'
import type {
  AxiosInstance,
  AxiosResponse,
  AxiosRequestConfig,
  InternalAxiosRequestConfig
} from 'axios'
import axios from 'axios'

import { EVENTS_NAME, COUNT_REMOVE_CONTROLLER, REQUEST_TIMEOUT } from './constants'
import HotClubHttpEvents from './hotClubHttpEvents'
import HotClubHttpGlobals from './hotClubHttpGlobals'
import type { OptionsParams } from './types'

/**
 * HTTP Client class for managing instances and global request handling with React Query caching.
 */
class HotClubHttp {
  private axiosInstance: AxiosInstance
  private static globalControllers: AbortController[] = []
  private defaultHeaders: Record<string, string> = {}
  private baseURL: string
  private instanceName: string
  private debugMode: boolean
  private events: typeof HotClubHttpEvents
  public queryClient: QueryClient

  /**
   * Private constructor to initialize an instance with specified baseURL and headers.
   * @param baseURL - The base URL for the Axios instance.
   * @param options
   * @param headers - Optional headers to set as defaults.
   * @param cacheConfig - Optional configuration for React Query's caching behavior.
   */
  private constructor(key: string, baseURL: string, options?: OptionsParams) {
    this.instanceName = key
    this.debugMode = options?.debug || false
    this.baseURL = baseURL
    this.defaultHeaders = options?.headers || {}
    this.queryClient = HotClubHttpGlobals.initQueryClient({ debugMode: this.debugMode })
    this.events = HotClubHttpEvents

    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
      headers: {
        ...this.defaultHeaders
      },
      timeout: REQUEST_TIMEOUT
    })

    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const globalHeaders = HotClubHttpGlobals.getGlobalHeaders()

        config.headers = {
          ...config.headers,
          ...globalHeaders
        }

        this.debugMode && Logger.info(`${this.instanceName} request`, { config })

        this.events.emit(EVENTS_NAME.REQUEST_SUCCESS, {
          ...config,
          instanceName: this.instanceName
        })

        return config
      },
      (error) => {
        this.events.emit(EVENTS_NAME.REQUEST_ERROR, { ...error, instanceName: this.instanceName })

        return Promise.reject(error)
      }
    )

    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        HotClubHttp.removeGlobalController(response.config)
        this.debugMode && Logger.info(`${this.instanceName} response`, { response })

        this.events.emit(EVENTS_NAME.RESPONSE_SUCCESS, {
          ...response,
          instanceName: this.instanceName
        })

        return response
      },
      (error) => {
        this.events.emit(EVENTS_NAME.RESPONSE_ERROR, { ...error, instanceName: this.instanceName })

        setTimeout(() => HotClubHttp.removeGlobalController(error.config), COUNT_REMOVE_CONTROLLER)

        return Promise.reject(error)
      }
    )
  }

  /**
   * Removes the AbortController for a request after completion or error.
   * @param config - Axios request config for the completed or errored request.
   */
  private static removeGlobalController(config: AxiosRequestConfig) {
    try {
      const index = HotClubHttp.globalControllers.findIndex(
        (controller) => controller.signal === config.signal
      )
      if (index > -1) {
        HotClubHttp.globalControllers.splice(index, 1)
      }
    } catch (e) {
      Logger.error('removeGlobalController', e)
      throw e
    }
  }

  /**
   * This method allows you to make GET requests to the specified URL, and provides options for setting custom headers.
   *
   * @param {string} url - The URL for the GET request.
   * @param {OptionsParams} [options] - Optional configuration for the request, including custom headers.
   * @param {Record<string, string>} [options.headers] - Custom headers to be sent with the request.
   *
   * @returns {Promise<AxiosResponse<TData>>} - A promise that resolves with the response from the HTTP request.
   *
   * @throws {Error} Throws an error if the request fails.
   */
  public async get<TData = unknown>(
    url: string,
    options?: OptionsParams
  ): Promise<AxiosResponse<TData>> {
    const headers = options?.headers || {}
    const controller = new AbortController()
    HotClubHttp.globalControllers.push(controller)

    const response = await this.axiosInstance.get<TData>(url, {
      signal: controller.signal,
      headers: { ...this.defaultHeaders, ...headers }
    })

    return response
  }

  /**
   * This method allows you to make POST requests to the specified URL with a provided payload (`data`), and custom headers.
   *
   * @param {string} url - The URL for the POST request.
   * @param {Object} body - The data (payload) to send in the body of the request.
   * @param {Object} [options] - Optional configuration for the request, including custom headers.
   * @param {Record<string, string>} [options.headers] - Custom headers to be sent with the request.
   *
   * @returns {Promise<AxiosResponse<TData>>} - A promise that resolves with the response from the HTTP request.
   *
   * @throws {Error} Throws an error if the request fails.
   */
  public async post<TData = unknown>(
    url: string,
    body: object,
    options?: {
      headers: Record<string, string>
    }
  ): Promise<AxiosResponse<TData>> {
    const headers = options?.headers || {}
    const controller = new AbortController()
    HotClubHttp.globalControllers.push(controller)

    const config: AxiosRequestConfig = {
      signal: controller.signal,
      headers: { ...this.defaultHeaders, ...headers }
    }

    const response = this.axiosInstance.post<TData>(url, body, config)
    HotClubHttp.removeGlobalController(config)

    return response
  }

  /**
   * This method allows you to make PUT requests to the specified URL with a provided payload (`data`), and custom headers.
   *
   * @param {string} url - The URL for the PUT request.
   * @param {Object} body - The data (payload) to send in the body of the request.
   * @param {Object} [options] - Optional configuration for the request, including custom headers.
   * @param {Record<string, string>} [options.headers] - Custom headers to be sent with the request.
   *
   * @returns {Promise<AxiosResponse<TData>>} - A promise that resolves with the response from the HTTP request.
   *
   * @throws {Error} Throws an error if the request fails.
   */
  public async put<TData = unknown>(
    url: string,
    body: object,
    options?: { headers?: Record<string, string> }
  ): Promise<AxiosResponse<TData>> {
    const headers = options?.headers || {}
    const controller = new AbortController()
    HotClubHttp.globalControllers.push(controller)

    const config: AxiosRequestConfig = {
      signal: controller.signal,
      headers: { ...this.defaultHeaders, ...headers }
    }

    const response = await this.axiosInstance.put<TData>(url, body, config)
    HotClubHttp.removeGlobalController(config)

    return response
  }

  /**
   * This method allows you to make PATCH requests to the specified URL with a provided payload (`data`), and custom headers.
   *
   * @param {string} url - The URL for the PATCH request.
   * @param {Object} body - The data (payload) to send in the body of the request.
   * @param {Object} [options] - Optional configuration for the request, including custom headers.
   * @param {Record<string, string>} [options.headers] - Custom headers to be sent with the request.
   *
   * @returns {Promise<AxiosResponse<TData>>} - A promise that resolves with the response from the HTTP request.
   *
   * @throws {Error} Throws an error if the request fails.
   */
  public async patch<TData = unknown>(
    url: string,
    body: object,
    options?: { headers?: Record<string, string> }
  ): Promise<AxiosResponse<TData>> {
    const headers = options?.headers || {}
    const controller = new AbortController()
    HotClubHttp.globalControllers.push(controller)

    const config: AxiosRequestConfig = {
      signal: controller.signal,
      headers: { ...this.defaultHeaders, ...headers }
    }

    const response = await this.axiosInstance.patch<TData>(url, body, config)
    HotClubHttp.removeGlobalController(config)

    return response
  }

  /**
   * This method allows you to make DELETE requests to the specified URL with optional custom headers.
   *
   * @param {string} url - The URL for the DELETE request.
   * @param {Object} [options] - Optional configuration for the request, including custom headers.
   * @param {Record<string, string>} [options.headers] - Custom headers to be sent with the request.
   *
   * @returns {Promise<AxiosResponse<TData>>} - A promise that resolves with the response from the HTTP request.
   *
   * @throws {Error} Throws an error if the request fails.
   */
  public async delete<TData = unknown>(
    url: string,
    options?: { headers?: Record<string, string> }
  ): Promise<AxiosResponse<TData>> {
    const headers = options?.headers || {}
    const controller = new AbortController()
    HotClubHttp.globalControllers.push(controller)

    const config: AxiosRequestConfig = {
      signal: controller.signal,
      headers: { ...this.defaultHeaders, ...headers }
    }

    const response = await this.axiosInstance.delete<TData>(url, config)
    HotClubHttp.removeGlobalController(config)

    return response
  }

  /**
   * Manually cancels all requests for this instance only.
   */
  public cancelRequestsManually() {
    HotClubHttp.globalControllers.forEach((controller) => controller?.abort())
    HotClubHttp.globalControllers = []
  }

  /**
   * Retrieves or creates a shared HotClubHttp instance for a given key.
   * @param key - Identifier for the shared HotClubHttp instance.
   * @param baseURL - The base URL for the instance.
   * @param headers - Optional default headers for the instance.
   * @returns Shared HotClubHttp instance.
   */
  public static getInstance(key: string, baseURL: string, options?: OptionsParams): HotClubHttp {
    try {
      if (!isBrowser()) return {} as HotClubHttp

      HotClubHttpGlobals.initGlobalStructure()

      /** check if instance exists before create */
      const hasInstance = HotClubHttpGlobals.getInstanceByKey(key)
      if (hasInstance) {
        hasInstance.debugMode &&
          Logger.info('getInstance:Instance already exists, returning...', { key })

        return hasInstance
      }

      const globalHeaders = HotClubHttpGlobals.getGlobalHeaders()

      const instance = new HotClubHttp(key, baseURL, {
        ...options,
        headers: {
          ...options?.headers,
          ...globalHeaders
        }
      })

      return HotClubHttpGlobals.addInstanceByKey(key, instance)
    } catch (e) {
      Logger.error('getInstance', e)
      throw e
    }
  }
}

export default HotClubHttp
